import { LitElement, html } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';

class CollectionCard extends LitElement {
  static properties = {
    storageKey: { type: String, attribute: 'storage_key' },
    products: { type: Array },
    selected_variants: { type: Object },
    collection: { type: String, reflect: true },
    columnsDesktop: { type: String, attribute: 'columns-desktop' },
    cardGap: { type: String, attribute: 'card-gap' },
    sizeStyle: { type: String, attribute: 'size-style' },
    enableLoadMore: { type: String, attribute: 'enable-load-more' },
    initialCount: { type: Number, attribute: 'initial-count' },
    loadingProductId: { type: Number },
    currencySymbol: { type: String, attribute: 'currency-symbol' },
    currencyCode: { type: String, attribute: 'currency-code' },
    showBadges: { type: String, attribute: 'show-badges' },
    loadMoreCount: { type: Number, attribute: 'load-more-count' },
    dataFetchComplete: { type: Boolean },
  };

  constructor() {
    super();
    this.products = [];
    this.selected_variants = {};
    this.cardGap = 'default';
    this.activeOverlayId = null;
    this.sizeStyle = 'white';
    this.enableLoadMore = false;
    this.initialCount = 0;
    this.displayedProducts = [];
    this.showCount = 0;
    this.columnsDesktop = '5';
    this.loadingProductId = null;
    this.currencySymbol = '$';
    this.currencyCode = 'AUD';
    this.dataLoaded = false;
    this.showBadges = 'true';
    this.loadMoreCount = 10;
    this.dataFetchComplete = false;
  }

  createRenderRoot() {
    return this;
  }

  updated(changed) {
    if (changed.has('collection')) {
      this.products = [];
      this.showCount = 0;
      this.loadProductsFromStorage();
    }
  }

  async firstUpdated() {
    if (this.collection) {
      try {
        const data = await window.WFDB.getItem(window.WFDB.STORES.PRODUCTS_NOLAYOUT, this.storageKey);
        if (data) {
          this.loadProductsFromStorage();
        }
      } catch (error) {
        console.warn('Error reading from IndexedDB:', error);
      }
    }

    document.addEventListener('collection_changed', (e) => {
      this.collection = e.detail.collection;
    });
  }

  async loadProductsFromStorage() {
    try {
      const data = await window.WFDB.getItem(window.WFDB.STORES.PRODUCTS_NOLAYOUT, this.storageKey);

      if (!data || !data.products) {
        console.warn('No products found in IndexedDB for key:', this.storageKey);
        return;
      }

      let products;
      if (this.collection === 'search_results') {
        const sr = data.products && data.products['search_results'];
        if (!sr) {
          return;
        }
        products = sr;
      } else {
        products = data.products[this.collection] || [];
      }

      this.products = products.map(p => ({
        ...p,
        sizes: [...new Set((p.variants || []).map(v => v.title.split('/')[0]))]
      }));

      if (this.enableLoadMore === 'enabled') {
        const currentShowCount = this.showCount || this.initialCount;
        this.showCount = currentShowCount;
        this.displayedProducts = this.products.slice(0, currentShowCount);
      } else {
        this.displayedProducts = [...this.products];
      }

      this.dataLoaded = true;
      this.requestUpdate();
    } catch (error) {
      console.error('Error loading products from IndexedDB:', error);
    }
  }

  async handleSizeClick(id, size) {
    const p = this.products.find((p) => p.id === id);
    if (!p) return;
    const v = p.variants.find((v) => v.title.split('/')[0] === size);
    if (!v || !v.available) return;

    this.loadingProductId = id;
    this.requestUpdate();

    try {
      if (this.storageKey === 'outfit_products') {
        document
          .querySelector('outfit-build')
          ?.addItem({ variantId: v.id, image: p.ghost_img });
      } else {
        await window.cartUtils.addItem(v.id, 1);
        window.drawerFunctions.toggleDrawer('drawer_sidecart', true);
      }
      this.selected_variants[id] = v.id;
    } catch (err) {
      console.error('Error adding to cart:', err);
    }

    this.loadingProductId = null;
    this.requestUpdate();
  }

  toggleOverlay(productId) {
    const willOpen = this.activeOverlayId !== productId;
    this.activeOverlayId = willOpen ? productId : null;
    this.requestUpdate();
    if (willOpen) {
      this.updateComplete?.then(() => {
        const overlayEl = this.querySelector(`#overlay_${productId}`);
        if (overlayEl) {
          const focusable = overlayEl.querySelector('button, a, [tabindex]:not([tabindex="-1"])');
          if (focusable && typeof focusable.focus === 'function') {
            focusable.focus();
          }
        }
      });
    }
  }

  // Helpers to keep template concise
  getOverlayState(product) {
    const overlayId = `overlay_${product.id}`;
    const isOpen = this.activeOverlayId === product.id;
    return {
      overlayId,
      isOpen,
      ariaHidden: isOpen ? 'false' : 'true',
      ariaExpanded: isOpen ? 'true' : 'false',
      tabIndex: isOpen ? '0' : '-1'
    };
  }

  renderProductImage(product) {
    return html`
      <a href="/products/${product.handle}" aria-hidden="true" tabindex="-1">
        <img
          src="${this.storageKey === 'outfit_products' ? product.ghost_img : product.image}"
          alt=""
          loading="lazy"
        />
      </a>
    `;
  }

  renderSizeOption(product, variant, isOpen, isSelected) {
    const tabIndex = isOpen ? '0' : '-1';
    const sizeLabel = variant.title.split('/')[0];
    if (!variant.available) {
      return html`
        <li role="listitem" class="size_option_item">
          <a
            href="${variant.variant_url}"
            class="collection_card_size_option sold_out"
            aria-label="Sold out – ${product.title} ${sizeLabel}"
            tabindex="${tabIndex}"
          >${sizeLabel}</a>
        </li>
      `;
    }
    return html`
      <li role="listitem" class="size_option_item">
        <button
          type="button"
          class="collection_card_size_option ${isSelected ? 'selected' : ''}"
          aria-label="Add ${product.title} size ${sizeLabel} to bag"
          aria-pressed="${isSelected ? 'true' : 'false'}"
          tabindex="${tabIndex}"
          @click=${() => this.handleSizeClick(product.id, sizeLabel)}
        >${sizeLabel}</button>
      </li>
    `;
  }

  renderSizeOptions(product, isOpen) {
    const sizes = product.sizes || [];
    return sizes.map((sz) => {
      const variant = product.variants.find((v) => v.title.split('/')[0] === sz);
      if (!variant) return null;
      const isSelected = this.selected_variants[product.id] === variant.id;
      return this.renderSizeOption(product, variant, isOpen, isSelected);
    }).filter(Boolean);
  }

  renderOverlay(product) {
    const { overlayId, isOpen, ariaHidden, ariaExpanded } = this.getOverlayState(product);
    return html`
      <button
        class="sr_only_quick_add_btn"
        style="position:absolute;left:-9999px;width:1px;height:1px;overflow:hidden;white-space:nowrap;"
        aria-label="Quick add sizes for ${product.title}"
        aria-expanded="${ariaExpanded}"
        aria-controls="${overlayId}"
        @click=${() => this.toggleOverlay(product.id)}
      >Quick add</button>
      <div id="${overlayId}" class="collection_card_size_overlay ${this.sizeStyle} ${isOpen ? 'visible' : ''}" aria-hidden="${ariaHidden}">
        ${this.loadingProductId === product.id
          ? html`<bouncing-ellipsis></bouncing-ellipsis>`
          : this.sizeStyle === 'black'
            ? html`
                <div class="quick_add_title">QUICK ADD TO BAG</div>
                <ul class="size_options_wrapper" role="list">
                  ${this.renderSizeOptions(product, isOpen)}
                </ul>
              `
            : html`
                <ul class="size_options_wrapper" role="list">
                  ${this.renderSizeOptions(product, isOpen)}
                </ul>
              `}
      </div>
      <button
        class="mob_quick_add_btn"
        aria-label="Quick add sizes for ${product.title}"
        aria-expanded="${ariaExpanded}"
        aria-controls="${overlayId}"
        @click=${() => this.toggleOverlay(product.id)}
      >+ Quick add</button>
    `;
  }

  renderCard(product, index) {
    return html`
      <li class="collection_card" role="listitem" style="animation-delay: ${(index % this.loadMoreCount) * 0.08}s;">
        <div class="collection_card_image_wrapper">
          ${this.renderProductImage(product)}
          ${this.showBadges === 'true' && product.tags ? html`<product-badges .tags=${product.tags} product-id="${product.id}" .comingSoon=${product.metafields?.state?.coming_soon?.value} .justDropped=${product.metafields?.state?.just_dropped?.value}></product-badges>` : ''}
          ${this.renderOverlay(product)}
        </div>
        <div class="collection_card_info">
          <div class="collection_card_title">
            <a href="/products/${product.handle}" aria-label="${product.title}">${product.title}</a>
            <wishlist-button
              product-image="${product.img}"
              product-id=${product.id}
              product-title=${ifDefined(product?.title)}
              product-category=${ifDefined(product?.category)}
            ></wishlist-button>
          </div>
          <div class="collection_card price">
            ${product.compare_at_price && parseFloat(product.compare_at_price) > parseFloat(product.price)
              ? html`
                  <span class="original_price">${this.currencySymbol}${parseFloat(product.compare_at_price).toFixed(2)}</span>
                  <span class="sale_price_group">
                    ${this.currencySymbol}${parseFloat(product.price).toFixed(2)} ${this.currencyCode}
                    <span class="discount">${Math.round((1 - parseFloat(product.price) / parseFloat(product.compare_at_price)) * 100)}% OFF</span>
                  </span>
                `
              : html`<span class="regular_price">${this.currencySymbol}${parseFloat(product.price).toFixed(2)} ${this.currencyCode}</span>`}
          </div>
        </div>
      </li>
    `;
  }

  focusFirstNewCard(previouslyRenderedCount, buttonEl) {
    const allCards = Array.from(this.querySelectorAll('.collection_card'));
    const newlyAdded = allCards.slice(previouslyRenderedCount);
    const firstNew = newlyAdded[0];
    if (firstNew) {
      const focusable = firstNew.querySelector('a, button, [tabindex]:not([tabindex="-1"])');
      if (focusable && typeof focusable.focus === 'function') {
        focusable.focus();
      } else {
        firstNew.setAttribute('tabindex', '-1');
        firstNew.focus();
      }
    }
    if (buttonEl) buttonEl.removeAttribute('aria-busy');
  }

  loadMoreProducts(e) {
    const previouslyRenderedCount = this.displayedProducts.length;
    const nextCount = this.showCount + this.loadMoreCount;
    this.showCount = nextCount;
    this.displayedProducts = this.products.slice(0, nextCount);
    e.target.setAttribute('aria-busy', 'true');
    this.requestUpdate();
    this.updateComplete?.then(() => {
      this.focusFirstNewCard(previouslyRenderedCount, e.target);
    });
  }

  render() {
    const isLoading = !this.dataLoaded;
    const hasNoResults = this.dataLoaded && this.dataFetchComplete && this.displayedProducts.length === 0;
    return html`
      <ul
        id="collection_cards_panel"
        class="collection_cards ${this.cardGap === 'none'
          ? 'no_gap'
          : 'default_gap'} columns-${this.columnsDesktop}"
        role="list"
      >
        ${isLoading
          ? html`
              <div class="initial_collection_spinner" role="status" aria-label="Loading products...">
                <img
                  src=${theme.images.loading}
                  alt="Loading..."
                  class="loading_gif"
                />
              </div>
            `
          : hasNoResults
          ? html`
              <div class="no_results_message">
                <p>No products match this filter.</p>
              </div>
            `
          : ''}
        ${this.displayedProducts.map((p, i) => this.renderCard(p, i))}
      </ul>
      ${this.enableLoadMore === 'enabled' && this.displayedProducts.length < this.products.length ? html`
        <div class="load_more_container">
          <button class="load_more_btn btn btn-primary" @click=${this.loadMoreProducts.bind(this)}>
            Load More
          </button>
        </div>
      ` : ''}
    `;
  }
}

customElements.define('collection-card', CollectionCard);
